'use client'

import classes from './ErrorPage.module.css'
import Image from 'next/image'
import { Heading, Paragraph } from '@/components/shared/typography'
import { EveLogo } from '../eve-logo'

interface ErrorPageProps {
  children?: React.ReactNode
  backgroundImage: string
  title?: string
  description?: string
  image: {
    src: string
    alt: string
    width: number
    height: number
  }
  variant?: '404' | '500'
  fullScreen?: boolean
}

export function ErrorPage({
  children,
  backgroundImage,
  title,
  description,
  image: { src, alt, width, height },
  variant,
  fullScreen = false,
}: ErrorPageProps) {
  // Default content for 404 variant
  const is404 = variant === '404'
  const defaultTitle = is404 ? 'Page not found' : undefined
  const defaultDescription = is404
    ? "This destination does not exist, or has been obliterated. We recommend using a simplified Jump Drive developed for exactly this scenario; your browser's 'back' button."
    : undefined

  const displayTitle = title ?? defaultTitle
  const displayDescription = description ?? defaultDescription

  const bgWrapperClass = fullScreen ? `${classes.bgWrapper} ${classes.bgWrapperFullScreen}` : classes.bgWrapper
  const bgImageClass = fullScreen ? `${classes.bgImage} ${classes.bgImageFullScreen}` : classes.bgImage

  return (
    <div className={`${classes.errorPageWrapper} ${fullScreen ? classes.errorPageWrapperFullScreen : ''}`}>
      <div className={bgWrapperClass}>
        <div className={bgImageClass} style={{ backgroundImage: `url(${backgroundImage})` }} />
        <div className={classes.childrenWrapper}>
          <div className={classes.mainContainer}>
            {fullScreen && (
              <div className={classes.eveLogoContainer}>
                <EveLogo />
              </div>
            )}
            <Image
              alt={alt}
              src={src}
              sizes={'100vw'}
              width={width}
              height={height}
              style={{ width: '100%', height: 'auto' }}
              priority
            />
            {displayTitle && displayDescription && (
              <div className={classes.textContainer}>
                <Heading variant="semi-bold-48" as="h2" uppercase className={classes.heading}>
                  {displayTitle}
                </Heading>
                <Paragraph responsive={{ maxSize: 22, minSize: 18 }} variant="regular-22" className={classes.paragraph}>
                  {displayDescription}
                </Paragraph>
              </div>
            )}
          </div>
        </div>
      </div>
      {children}
    </div>
  )
}

// Preset configurations for common error types
export function NotFoundErrorPage() {
  return (
    <ErrorPage
      variant="404"
      title="Page not found"
      description="This destination does not exist, or has been obliterated. We recommend using a simplified Jump Drive developed for exactly this scenario; your browser's 'back' button."
      backgroundImage="https://images.ctfassets.net/7lhcm73ukv5p/4xd1Q5VkxP2idy4CTJtqQC/0f13926f7190b976aa3680db76c03e96/404-background.jpg?w=1700&q=75&fm=webp"
      image={{
        src: 'https://images.ctfassets.net/7lhcm73ukv5p/57yVZ6hQQ5eXRkIAF2UTcl/d6f2270bb7d7a09ecd2b9db55422fc54/404-image.png',
        alt: '404',
        width: 999,
        height: 555,
      }}
    />
  )
}

export function ServerErrorPage() {
  return (
    <ErrorPage
      variant="500"
      title="Server error"
      description="An error occurred while processing your request. Please try again later."
      backgroundImage="https://images.ctfassets.net/7lhcm73ukv5p/10QLcwvb8spYPVxV0wJqT1/e55d062461b38ee851767521857c68d0/error-background.jpg?w=1700&q=75&fm=webp"
      image={{
        src: 'https://images.ctfassets.net/7lhcm73ukv5p/5FBtjx2gCzFepESfDVmZYb/2af5ccf07c6b9af1a05df3d3cd14ba94/500-text.png',
        alt: '500',
        width: 999,
        height: 555,
      }}
      fullScreen
    />
  )
}

export function RouteErrorPage() {
  return (
    <ErrorPage
      variant="500"
      title="Server error"
      description="An error occurred while processing your request. Please try again later."
      backgroundImage="https://images.ctfassets.net/7lhcm73ukv5p/10QLcwvb8spYPVxV0wJqT1/e55d062461b38ee851767521857c68d0/error-background.jpg?w=1700&q=75&fm=webp"
      image={{
        src: 'https://images.ctfassets.net/7lhcm73ukv5p/5FBtjx2gCzFepESfDVmZYb/2af5ccf07c6b9af1a05df3d3cd14ba94/500-text.png',
        alt: '500',
        width: 999,
        height: 555,
      }}
      fullScreen={false}
    />
  )
}
